/**
 * VncConsole.tsx
 *
 * Standalone noVNC console page rendered at /vnc/:sessionId.
 *
 * Flow:
 *   1. Pull sessionId from the route
 *   2. GET /api/vnc/session/:sessionId for config (vmid, hostname, wsPath)
 *   3. Load noVNC's RFB module from /novnc/core/rfb.js (ES module)
 *   4. Connect to wss://<host>/api/vnc/ws/<sessionId>
 *   5. Hook the canvas + status updates into the page
 *
 * The noVNC library + assets live under public/novnc/ so they're served
 * directly from our origin (no CDN dep, no Proxmox URL leak).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

interface SessionInfo {
  sessionId: string;
  vmid: number;
  hostname: string;
  wsPath: string;
  vncPassword: string;
  expiresAt: string;
}

const VncConsole: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const screenRef = useRef<HTMLDivElement>(null);
  const rfbRef = useRef<any>(null);
  const [info, setInfo] = useState<SessionInfo | null>(null);
  const [status, setStatus] = useState<string>('Connecting…');
  const [err, setErr] = useState('');
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        // 1. session config — auth-gated: the endpoint returns console
        // credentials, so it requires the logged-in owner's token.
        const token = localStorage.getItem('token');
        const resp = await fetch(`/api/vnc/session/${sessionId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const j = await resp.json();
        if (!j.success) {
          setErr(j.error || 'Session not found or expired');
          return;
        }
        setInfo(j);

        // 2. load noVNC RFB from public/
        const mod: any = await import(/* webpackIgnore: true */ '/novnc/core/rfb.js' as any);
        const RFB = mod.default || mod.RFB;
        if (!RFB) {
          setErr('Failed to load noVNC client library');
          return;
        }

        // 3. compute WSS url
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${wsProtocol}//${window.location.host}${j.wsPath}`;

        // 4. instantiate RFB
        if (!screenRef.current) return;
        // Proxmox uses the vncticket as both the WS query string AND the
        // RFB password during VNC security negotiation. Without this you get
        // "Security negotiation failed (reason: password check failed!)".
        const rfb = new RFB(screenRef.current, wsUrl, {
          credentials: { password: j.vncPassword || '' },
        });
        // scaleViewport keeps the VM canvas readable when the iframe is
        // smaller than the VM's framebuffer. resizeSession asks the VM to
        // change its resolution on the fly; Proxmox's default QEMU display
        // adapter rejects this ("Resize is administratively prohibited"),
        // which floods the console with noise — leave it off.
        rfb.scaleViewport = true;
        rfb.resizeSession = false;
        rfb.showDotCursor = true;
        rfb.background = '#1f2937';

        rfb.addEventListener('connect', () => {
          setStatus('Connected');
          setConnected(true);
        });
        rfb.addEventListener('disconnect', (e: any) => {
          setConnected(false);
          if (e.detail?.clean) {
            setStatus('Disconnected');
          } else {
            setStatus('Connection lost');
            setErr('The console connection was interrupted. Close this tab and click Console again from the VPS panel for a fresh session.');
          }
        });
        rfb.addEventListener('credentialsrequired', () => {
          setErr('This console requires a VNC password we do not have. Contact support.');
        });
        rfb.addEventListener('securityfailure', (e: any) => {
          setErr(`VNC security failure: ${e.detail?.reason || 'unknown'}`);
        });

        rfbRef.current = rfb;
      } catch (e: any) {
        console.error(e);
        setErr(e.message || 'Failed to start console');
      }
    })();

    return () => {
      if (rfbRef.current) {
        try { rfbRef.current.disconnect(); } catch { /* */ }
        rfbRef.current = null;
      }
    };
  }, [sessionId]);

  const sendCtrlAltDel = () => {
    if (rfbRef.current && connected) {
      try { rfbRef.current.sendCtrlAltDel(); } catch { /* */ }
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Top bar */}
      <div className="bg-gray-800 text-white px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="font-semibold">VPS Console</div>
          {info && (
            <div className="text-xs text-gray-400">
              {info.hostname} (vmid {info.vmid})
            </div>
          )}
          <div className={`text-xs px-2 py-0.5 rounded ${
            connected ? 'bg-green-700 text-green-100' :
            err ? 'bg-red-700 text-red-100' :
            'bg-amber-700 text-amber-100'
          }`}>
            {connected ? 'Connected' : err ? 'Error' : status}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={sendCtrlAltDel}
            disabled={!connected}
            className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            title="Send Ctrl+Alt+Delete to the VM"
          >
            Ctrl+Alt+Del
          </button>
          <button
            onClick={() => window.close()}
            className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded"
          >
            Close
          </button>
        </div>
      </div>

      {/* Error banner */}
      {err && (
        <div className="bg-red-900 text-red-100 px-4 py-2 text-sm border-b border-red-800">
          {err}
        </div>
      )}

      {/* Console canvas */}
      <div
        ref={screenRef}
        className="flex-1 overflow-hidden"
        style={{ background: '#1f2937' }}
      />

      {/* Footer */}
      <div className="bg-gray-800 text-gray-400 text-xs px-4 py-1.5 flex-shrink-0 border-t border-gray-700">
        Session expires in 5 minutes. Mouse + keyboard go directly to the VM.
      </div>
    </div>
  );
};

export default VncConsole;
